import React, { ReactNode, useRef, type JSX } from 'react';

import {
    setRoute,
    setWidgetName,
    setWidgetSource,
    toggleDeviceLinkMode,
} from '@fiji/common/src/features/widgetManagement/widgetConfigurationSlice';
import Link from '@mui/icons-material/Link';
import { useTheme } from '@mui/material/styles';
import { DataSourceModal } from 'pages/DashboardAndWidgets';
import { useWidgetConfigHandlers } from '../common';
import { useTypedSelector, useAppDispatch } from '@fiji/common';
import { Box, Icon, Stack, Switch, Typography } from '@mui/material';
import { CustomTransComponent } from 'components';
import { setLoadsPreferences } from '@fiji/common/src/features/widgetManagement/loadsWidgetSlice';
import { setLoadGroupsPreferences } from '@fiji/common/src/features/widgetManagement/loadGroupWidgetSlice';
import { setLoadGroupTrendsPreferences } from '@fiji/common/src/features/widgetManagement/loadGroupTrendsSlice';

const buildWidgetRoutePayload = (currentRealmName: string, selectedNodeId: string, widgetTypeId: string): any => ({
    url: `/${currentRealmName}/device/${selectedNodeId}`,
    ...(widgetTypeId !== 'charts' && { state: widgetTypeId }),
});

const shouldSetWidgetRoute = (widgetTypeId: string | undefined): boolean => widgetTypeId !== 'map';

const shouldToggleDeviceLinkFromSource = (sourceId: unknown, source: unknown, isMultiDevice: boolean): boolean =>
    !sourceId && Boolean(source) && isMultiDevice;

const shouldOpenDataSourceModal = (sourceId: unknown, isMultiDevice: boolean, type: string | undefined): boolean =>
    !sourceId && isMultiDevice && type === 'source';

const syncWidgetRouteAndName = (
    dispatch: any,
    widgetDetails: any,
    currentRealmName: string,
    selectedNodeId: string
): void => {
    if (!widgetDetails) return;

    const widgetTypeId = widgetDetails?.widgetType?.id;
    if (shouldSetWidgetRoute(widgetTypeId)) {
        dispatch(setRoute(buildWidgetRoutePayload(currentRealmName, selectedNodeId, widgetTypeId)));
    }

    dispatch(setWidgetName(widgetDetails?.name));
};

const toggleDeviceLinkFromSource = (
    dispatch: any,
    sourceId: unknown,
    source: unknown,
    isMultiDevice: boolean
): void => {
    if (shouldToggleDeviceLinkFromSource(sourceId, source, isMultiDevice)) {
        dispatch(toggleDeviceLinkMode());
    }
};

const openDataSourceModalIfNeeded = (
    dataSourceModalRef: React.MutableRefObject<any>,
    sourceId: unknown,
    isMultiDevice: boolean,
    type: string | undefined,
    source: unknown
): void => {
    if (shouldOpenDataSourceModal(sourceId, isMultiDevice, type)) {
        dataSourceModalRef?.current?.handleModalAction(true, source);
    }
};

const resetAutomationPreferenceByWidgetType = (dispatch: any, widgetTypeId: string | undefined): void => {
    const automationPreferenceActionByType: Record<string, () => void> = {
        loads: () =>
            dispatch(
                setLoadsPreferences({
                    key: 'isAutomated',
                    value: false,
                })
            ),
        load_groups: () =>
            dispatch(
                setLoadGroupsPreferences({
                    key: 'isAutomated',
                    value: false,
                })
            ),
        load_group_trends: () =>
            dispatch(
                setLoadGroupTrendsPreferences({
                    key: 'isAutomated',
                    value: false,
                })
            ),
    };

    automationPreferenceActionByType[widgetTypeId ?? '']?.();
};

const renderSourceModal = (
    type: string | undefined,
    dataSourceModalRef: React.MutableRefObject<any>,
    dispatch: any
): JSX.Element | null =>
    type === 'source' ? (
        <DataSourceModal
            data-testid="common-configuration-data-source-modal"
            ref={dataSourceModalRef}
            type="edit"
            handleConfirm={(args: any): void => dispatch(setWidgetSource(args))}
        />
    ) : null;

const resolveHeadingContent = (heading?: ReactNode): ReactNode =>
    heading ?? <CustomTransComponent translationKey={'COMMON:LINK_DEVICES'} />;

type CommonConfigurationHeaderViewProps = {
    headingContent: ReactNode;
    theme: any;
    isMultiDeviceChecked: boolean;
    handleMultiDeviceToggle: (event: React.ChangeEvent<HTMLInputElement>) => void;
    sourceModal: JSX.Element | null;
};

type HeaderTitleProps = {
    headingContent: ReactNode;
};

const HeaderTitle = ({ headingContent }: HeaderTitleProps): JSX.Element => (
    <Box className="align-items-center cursor-pointer">
        <Icon>
            <Link color="primary" />
        </Icon>
        <Typography variant="body2" color="primary" fontWeight={'600'} fontSize={'14px'} mx={1}>
            {headingContent}
        </Typography>
    </Box>
);

type MultiDeviceSwitchProps = {
    theme: any;
    isMultiDeviceChecked: boolean;
    handleMultiDeviceToggle: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

const buildSwitchStateSx = (thumbColor: string, trackColor: string): any => ({
    color: thumbColor,
    '& + .MuiSwitch-track': {
        backgroundColor: trackColor,
    },
});

const SWITCH_SELECTOR_CHECKED = '& .MuiSwitch-switchBase.Mui-checked';
const SWITCH_SELECTOR_DISABLED_CHECKED = '& .MuiSwitch-switchBase.Mui-checked.Mui-disabled';

const getPrimaryPalette = (theme: any): any => theme?.palette?.primary;

const MultiDeviceSwitch = ({
    theme,
    isMultiDeviceChecked,
    handleMultiDeviceToggle,
}: MultiDeviceSwitchProps): JSX.Element => {
    const primaryPalette = getPrimaryPalette(theme);
    const switchSx = {
        [SWITCH_SELECTOR_CHECKED]: buildSwitchStateSx(primaryPalette?.main, primaryPalette?.main),
        [SWITCH_SELECTOR_DISABLED_CHECKED]: buildSwitchStateSx(primaryPalette?.[50], primaryPalette?.[200]),
    };

    return (
        <Switch
            data-testid="common-configuration-multi-device-toggle"
            sx={switchSx}
            checked={isMultiDeviceChecked}
            onChange={handleMultiDeviceToggle}
        />
    );
};

const CommonConfigurationHeaderView = ({
    headingContent,
    theme,
    isMultiDeviceChecked,
    handleMultiDeviceToggle,
    sourceModal,
}: CommonConfigurationHeaderViewProps): JSX.Element => (
    <>
        <Box px={3} py={'14px'} className="border-bottom-1 flex-center-between bg-header">
            <Stack direction={'row'} sx={{ width: '100%' }} justifyContent={'space-between'}>
                <HeaderTitle headingContent={headingContent} />
                <MultiDeviceSwitch
                    theme={theme}
                    isMultiDeviceChecked={isMultiDeviceChecked}
                    handleMultiDeviceToggle={handleMultiDeviceToggle}
                />
            </Stack>
        </Box>
        {sourceModal}
    </>
);

const getWidgetTypeId = (widgetDetails: any): string | undefined => widgetDetails?.widgetType?.id;

const createMultiDeviceToggleHandler = (
    dataSourceModalRef: React.MutableRefObject<any>,
    widgetDetails: any,
    type: string | undefined,
    dispatch: any
): ((event: React.ChangeEvent<HTMLInputElement>) => void) => {
    const widgetTypeId = getWidgetTypeId(widgetDetails);

    return (event: React.ChangeEvent<HTMLInputElement>): void => {
        openDataSourceModalIfNeeded(
            dataSourceModalRef,
            widgetDetails?.sourceId,
            !event.target.checked,
            type,
            widgetDetails?.source
        );
        dispatch(toggleDeviceLinkMode());
        resetAutomationPreferenceByWidgetType(dispatch, widgetTypeId);
    };
};

const useCommonConfigurationHeaderEffects = (
    dispatch: any,
    widgetDetails: any,
    currentRealmName: string,
    selectedNodeId: string,
    source: unknown,
    isMultiDevice: boolean
): void => {
    React.useEffect(() => {
        syncWidgetRouteAndName(dispatch, widgetDetails, currentRealmName, selectedNodeId);
    }, [widgetDetails, currentRealmName, selectedNodeId, dispatch]);

    React.useEffect(() => {
        toggleDeviceLinkFromSource(dispatch, widgetDetails?.sourceId, source, isMultiDevice);
    }, [source, widgetDetails?.sourceId, isMultiDevice, dispatch]);
};

export const CommonConfigurationHeader = ({ type, heading }: { type?: string; heading?: ReactNode }): JSX.Element => {
    const dispatch = useAppDispatch();
    const theme: any = useTheme();
    const dataSourceModalRef = useRef<any>(null);
    const currentRealmName = useTypedSelector((state) => state.common.selectedRealm);
    const source = useTypedSelector((state) => state['widgetConfiguraton']?.['source']);
    const selectedNode = useTypedSelector((state) => state['widgetConfiguraton']?.['device']);

    const {
        details: { widgetDetails },
    } = useWidgetConfigHandlers();

    const isMultiDevice = useTypedSelector((state) => state['widgetConfiguration']['data']['multiDevice']);

    useCommonConfigurationHeaderEffects(
        dispatch,
        widgetDetails,
        currentRealmName,
        selectedNode?.id,
        source,
        isMultiDevice
    );

    const handleMultiDeviceToggle = createMultiDeviceToggleHandler(dataSourceModalRef, widgetDetails, type, dispatch);

    const headingContent = resolveHeadingContent(heading);
    const sourceModal = renderSourceModal(type, dataSourceModalRef, dispatch);
    const isMultiDeviceChecked = Boolean(isMultiDevice);

    return (
        <CommonConfigurationHeaderView
            headingContent={headingContent}
            theme={theme}
            isMultiDeviceChecked={isMultiDeviceChecked}
            handleMultiDeviceToggle={handleMultiDeviceToggle}
            sourceModal={sourceModal}
        />
    );
};
